import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ContainerFluid } from 'common/styles';
import { Header } from 'modules/core/components/Header/Header';
import { EMPTY_STRING, ICONS_HEIGHT } from 'modules/core/constants';
import { PermissionWrapper } from 'modules/PermissionWrapper';
import { Icon, NavigationLink } from 'modules/core/components';
import { ALL } from 'modules/PermissionWrapper/roleGroups.const';
import { useSelector } from 'react-redux';
import { getAuthState, getUser } from 'store/selectors/auth.selector';
import { DealSourceProvider, Roles } from 'modules/core/enums';
import {
  getManagerRedirectPath,
  getSalespersonRedirectPath,
  getRolesDashboardNavigation,
} from 'router/const/dashboard.const';
import { getLendersList } from 'store/selectors/lenders.selector';
import { getDisabledFeaturesList } from 'store/selectors/ui.selector';
import { getDealSourceData, getDealSourceDataFlags } from 'store/selectors/RACAffordabilityEngine.selector';
import { ROUTER_PATH } from 'router/const/routerPath.const';
import { logToDatadog } from 'utils/datadog';
import { DatadogEvents } from 'utils/datadogEvents';

import { useLinkConfig } from './useLinkConfig';
import { ERROR_404_NOT_FOUND, ERROR_PAGE_BODY_MAP } from './error.const';
import { DealertrackSsoError, RouteOneSsoError } from './components';
import { ErrorBody, ErrorCard, ErrorHeader, ErrorLinks, ErrorWrapper } from './styles';

interface IErrorPage {
  errorCode: number;
}

export const ErrorPage: React.FC<IErrorPage> = ({ errorCode = ERROR_404_NOT_FOUND }) => {
  const { t } = useTranslation();
  const linksProps = useLinkConfig();
  const user = useSelector(getUser);
  const { isAlternativeSsoError } = useSelector(getAuthState);
  const lenders = useSelector(getLendersList);
  const disabledFeatures = useSelector(getDisabledFeaturesList);
  const dealSourceData = useSelector(getDealSourceData);
  const dealSourceDataFlags = useSelector(getDealSourceDataFlags);
  const hasLoggedDealertrackSsoError = useRef(false);

  const isDealertrackSso = user?.dealSource === DealSourceProvider.DEALER_TRACK;

  useEffect(() => {
    if (!user?.is_sso || !isDealertrackSso || hasLoggedDealertrackSsoError.current) return;

    hasLoggedDealertrackSsoError.current = true;

    const saleVehicle = dealSourceData?.sale_vehicle;
    let saleVehicleLog;

    if (saleVehicle) {
      saleVehicleLog = {
        year: saleVehicle.year,
        make: saleVehicle.make,
        model: saleVehicle.model,
        vin: saleVehicle.vin,
      };
    }

    logToDatadog(
      DatadogEvents.LOG_CATEGORIES[DatadogEvents.DEALERTRACK_SSO_ERROR_PAGE],
      {
        log_category: DatadogEvents.DEALERTRACK_SSO_ERROR_PAGE,
        error_code: errorCode,
        role: user.role,
        is_alternative_sso: user.isAlternativeSso || false,
        is_alternative_sso_error: isAlternativeSsoError,
        dealer: {
          id: user.dealer?.id,
          shortname: user.dealer?.shortname,
          afg_dealer_id: user.afgDealerId,
        },
        request_identifiers: {
          session_tracking_id: user.sessionTrackingId || '',
          original_conversation_id: user.originalConversationId,
          original_external_dealer_id: user.originalExternalDealerId,
          original_external_lender_id: user.originalExternalLenderId,
          route_one_user_id: user.routeOneUserId,
        },
        deal_source_data: {
          lender: dealSourceData?.lender,
          lender_path: dealSourceData?.lender_path,
          conversation_id: dealSourceData?.conversation_id,
          dealer_id: dealSourceData?.dealer_id,
          deal_jacket_id: dealSourceData?.deal_jacket_id,
          vehicle_exists: dealSourceData?.vehicle_exists,
          vehicle_id: dealSourceData?.vehicle_id,
          transaction_type: dealSourceData?.transaction_type,
          sale_vehicle: saleVehicleLog,
        },
        deal_source_flags: {
          is_received: dealSourceDataFlags?.isReceived,
          is_invalid: dealSourceDataFlags?.isInvalid,
          is_get_invalid: dealSourceDataFlags?.isGetInvalid,
          message: dealSourceDataFlags?.message,
        },
        available_lenders: lenders.map((lender) => ({
          name: lender.name,
          ui_name: lender.uiName,
          path: lender.link || lender.path,
          route_one_id: lender.routeOneId,
        })),
        disabled_features: disabledFeatures,
        url: window.location.href,
        referrer: document.referrer || '',
      },
      user,
    );
  }, [
    user,
    isDealertrackSso,
    errorCode,
    isAlternativeSsoError,
    dealSourceData,
    dealSourceDataFlags,
    lenders,
    disabledFeatures,
  ]);

  if (user?.is_sso) {
    const { path } = getRolesDashboardNavigation(user, disabledFeatures, lenders);

    linksProps[linksProps.length - 1] = {
      ...linksProps[linksProps.length - 1],
      path: path,
    };
  } else if (user && [Roles.MANAGER, Roles.SALESPERSON].includes(user.role)) {
    linksProps[linksProps.length - 1] = {
      ...linksProps[linksProps.length - 1],
      get path() {
        if (user.role === Roles.MANAGER) {
          const path = getManagerRedirectPath(disabledFeatures, lenders);

          return path === EMPTY_STRING ? '/manager-admin/' : path;
        }

        return getSalespersonRedirectPath(disabledFeatures);
      },
    };
  }

  const links = useMemo(
    () =>
      linksProps.map(({ isExternal, path, iconSrc, allowedRoles, title }) => {
        if (path !== ROUTER_PATH.ERROR) {
          return (
            <PermissionWrapper allowedRoles={allowedRoles || ALL}>
              <Icon imageSrc={iconSrc} height={ICONS_HEIGHT.MIDDLE} />
              <NavigationLink isExternal={isExternal} path={path}>
                {title}
              </NavigationLink>
            </PermissionWrapper>
          );
        }

        return null;
      }),
    [linksProps],
  );

  return (
    <ContainerFluid fluid>
      <Header withoutSideBar />
      {user?.is_sso ? (
        isDealertrackSso ? (
          <DealertrackSsoError />
        ) : (
          <RouteOneSsoError />
        )
      ) : (
        <ErrorWrapper>
          <ErrorCard>
            <ErrorHeader>
              <h1>{`${errorCode} ${t('components.errorPage.errorTitle')}`}</h1>
            </ErrorHeader>
            <ErrorBody>
              <p>{ERROR_PAGE_BODY_MAP.get(errorCode)}</p>
              <p>{t('components.errorPage.message.contactAdministrator')}</p>
            </ErrorBody>
            <ErrorLinks>{links}</ErrorLinks>
          </ErrorCard>
        </ErrorWrapper>
      )}
    </ContainerFluid>
  );
};
